import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, ShoppingCart, User, Menu, X, Heart, LogOut, Globe, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAutoTranslation } from '../hooks/useAutoTranslation';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import LanguageSelector from "./LanguageSelector";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const userMenuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  
  const { t } = useTranslation();
  const { at } = useAutoTranslation();

  // Close user dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
      setIsMenuOpen(false);
    }
  };

  // Usar try-catch para lidar com erros do CartContext
  let itemCount = 0;
  try {
    const cartContext = useCart();
    itemCount = cartContext?.itemCount || 0;
  } catch (error) {
    // Cart context not ready yet
  }

  const { isAuthenticated, user, logout } = useAuth();

  const handleLogout = () => {
    logout();
    setIsUserMenuOpen(false);
  };

  const handleFavoritesClick = () => {
    if (!isAuthenticated) {
      navigate('/login?from=favorites');
    }
    // When favorites page exists: navigate('/favoritos')
  };

  return (
    <header className="bg-white shadow-lg sticky top-0 z-50">
      <div className="bg-[#4ECDC4] text-white py-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center text-sm">
            <div className="flex items-center space-x-4">
              <span>{t('filters.topBanner')}</span>
            </div>
            <div className="flex items-center space-x-4">
              <LanguageSelector />
              <span>✉️ fit@snatchedlatinas.ch</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-32">
          <div className="flex-shrink-0">
            <Link to="/">
              <img src="/images/logo.png" alt="Snatched Latinas" className="h-32 w-auto max-h-32" />
            </Link>
          </div>

          <nav className="hidden md:flex space-x-8">
            <Link to="/" className="text-gray-700 hover:text-[#4ECDC4] transition-colors font-medium">
              {t('nav.home')}
            </Link>
            <Link to="/produtos" className="text-gray-700 hover:text-[#4ECDC4] transition-colors font-medium">
              {t('nav.products')}
            </Link>
            <Link to="/sobre" className="text-gray-700 hover:text-[#4ECDC4] transition-colors font-medium">
              {at('Quem é Savana')}
            </Link>
            <Link to="/blog" className="text-gray-700 hover:text-[#4ECDC4] transition-colors font-medium">
              Blog
            </Link>
            <Link to="/contacto" className="text-gray-700 hover:text-[#4ECDC4] transition-colors font-medium">
              {t('nav.contact')}
            </Link>
          </nav>

          <div className="hidden lg:flex items-center flex-1 max-w-xs mx-8">
            <form onSubmit={handleSearch} className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('filters.searchPlaceholder')}
                className="w-64 pl-10 pr-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-[#4ECDC4] focus:border-transparent"
              />
              <button type="submit" className="absolute left-3 top-2.5">
                <Search className="h-5 w-5 text-gray-400" />
              </button>
            </form>
          </div>

          <div className="flex items-center space-x-4">
            <button
              onClick={handleFavoritesClick}
              className="relative p-2 text-gray-600 hover:text-[#4ECDC4] transition-colors"
              title={!isAuthenticated ? t('nav.loginForFavorites') : t('nav.favorites')}
            >
              <Heart className="h-6 w-6" />
            </button>

            <Link to="/carrinho" className="relative p-2 text-gray-600 hover:text-[#4ECDC4] transition-colors">
              <ShoppingCart className="h-6 w-6" />
              {itemCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-[#4ECDC4] text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {itemCount}
                </span>
              )}
            </Link>

            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                className="flex items-center space-x-2 p-2 text-gray-600 hover:text-[#4ECDC4] transition-colors"
              >
                <User className="h-6 w-6" />
                {isAuthenticated && user && (
                  <span className="hidden sm:inline text-sm font-medium">{user.name || user.email}</span>
                )}
              </button>

              {isUserMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
                  {isAuthenticated ? (
                    <>
                      <Link
                        to="/login"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        {at('Meu Perfil')}
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        {at('Sair')}
                      </button>
                    </>
                  ) : (
                    <Link
                      to="/login"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      {t('nav.login')}
                    </Link>
                  )}
                </div>
              )}
            </div>

            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 text-gray-600 hover:text-[#4ECDC4] transition-colors"
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {isMenuOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 border-t border-gray-200">
              <div className="relative mb-4">
                <form onSubmit={handleSearch}>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={t('filters.searchPlaceholder')}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-[#4ECDC4] focus:border-transparent"
                  />
                  <button type="submit" className="absolute left-3 top-2.5">
                    <Search className="h-5 w-5 text-gray-400" />
                  </button>
                </form>
              </div>

              <Link
                to="/"
                className="block text-gray-700 hover:text-[#4ECDC4] transition-colors font-medium py-2"
                onClick={() => setIsMenuOpen(false)}
              >
                {t('nav.home')}
              </Link>
              <Link
                to="/produtos"
                className="block text-gray-700 hover:text-[#4ECDC4] transition-colors font-medium py-2"
                onClick={() => setIsMenuOpen(false)}
              >
                {t('nav.products')}
              </Link>
              <Link
                to="/sobre"
                className="block text-gray-700 hover:text-[#4ECDC4] transition-colors font-medium py-2"
                onClick={() => setIsMenuOpen(false)}
              >
                {at('Quem é Savana')}
              </Link>
              <Link
                to="/blog"
                className="block text-gray-700 hover:text-[#4ECDC4] transition-colors font-medium py-2"
                onClick={() => setIsMenuOpen(false)}
              >
                Blog
              </Link>
              <Link
                to="/contacto"
                className="block text-gray-700 hover:text-[#4ECDC4] transition-colors font-medium py-2"
                onClick={() => setIsMenuOpen(false)}
              >
                {t('nav.contact')}
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
